import React, { useState } from 'react';
import axios from 'axios';
import { ZoomBtn, ZoomBadge } from './zoom/ZoomTable';

/* Call Trace (2026-10-29) — enter who dialed and what was dialed; the system checks every stage
   (caller, number, permissions, route, gateway, carrier), says where and why the call failed and
   draws the SIP ladder of each attempt. No commands to run: the server does the diagnostics. */
const box: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 14 };
const inp: React.CSSProperties = { padding: '9px 12px', border: '1px solid #cdd2e6', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const err = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';
const dubai = (d: string) => new Date(d).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
const TONE: Record<string, { bg: string; fg: string; badge: 'green' | 'red' | 'amber' | 'gray'; word: string }> = {
  ok: { bg: '#ecfdf5', fg: '#047857', badge: 'green', word: 'OK' },
  fail: { bg: '#fef2f2', fg: '#b91c1c', badge: 'red', word: 'Problem' },
  warn: { bg: '#fffbeb', fg: '#b45309', badge: 'amber', word: 'Check' },
  skip: { bg: '#f9fafb', fg: '#6b7280', badge: 'gray', word: '—' },
};

function Ladder({ a }: { a: any }) {
  const [open, setOpen] = useState<number | null>(null);
  if (!a.ladder || !a.ladder.length) {
    return <div style={{ fontSize: 13, color: '#6b7280', padding: '8px 0' }}>{a.sip_captured === false ? 'No SIP messages were captured for this call (calls made before SIP capture was switched on on 29 Oct 2026 have no ladder). Dial again and trace again to see the ladder.' : 'No SIP messages.'}</div>;
  }
  const nodes = a.nodes || [];
  const colW = 230, left = 90, rowH = 30, top = 52;
  const width = left + colW * Math.max(nodes.length, 2);
  const height = top + rowH * a.ladder.length + 16;
  const x = (k: string) => left + colW * nodes.findIndex((n: any) => n.key === k) + colW / 2;
  const t0 = new Date(a.ladder[0].t).getTime();
  const color = (m: any) => (m.method ? '#1d4ed8' : m.status >= 400 ? '#b91c1c' : m.status >= 200 ? '#047857' : '#6b7280');
  return (
    <div>
      <div style={{ overflowX: 'auto', border: '1px solid #eef0f5', borderRadius: 8 }}>
        <svg width={width} height={height} style={{ display: 'block', fontFamily: 'inherit' }}>
          <defs>{['#1d4ed8', '#b91c1c', '#047857', '#6b7280'].map((c) => (
            <marker key={c} id={'arr' + c.slice(1)} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill={c} /></marker>))}
          </defs>
          {nodes.map((n: any) => (
            <g key={n.key}>
              <text x={x(n.key)} y={18} textAnchor="middle" fontSize="13" fontWeight="700" fill="#111827">{n.label}</text>
              <text x={x(n.key)} y={34} textAnchor="middle" fontSize="11" fill="#6b7280">{n.ip}</text>
              <line x1={x(n.key)} y1={top - 8} x2={x(n.key)} y2={height - 6} stroke="#d1d5db" strokeDasharray="4 4" />
            </g>))}
          {a.ladder.map((m: any, i: number) => {
            const y = top + rowH * i + 14, x1 = x(m.from), x2 = x(m.to), c = color(m);
            return (
              <g key={m.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(open === i ? null : i)}>
                <rect x={0} y={y - 16} width={width} height={rowH} fill={open === i ? '#eff6ff' : 'transparent'} />
                <text x={8} y={y + 4} fontSize="11" fill="#6b7280">+{((new Date(m.t).getTime() - t0) / 1000).toFixed(3)}s</text>
                <line x1={x1} y1={y} x2={x2} y2={y} stroke={c} strokeWidth="1.6" markerEnd={`url(#arr${c.slice(1)})`} />
                <text x={(x1 + x2) / 2} y={y - 5} textAnchor="middle" fontSize="12" fontWeight="600" fill={c}>{m.label}</text>
              </g>);
          })}
        </svg>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', margin: '6px 0' }}>Click a message to see it in full. Blue = requests, green = success, red = errors.</div>
      {open !== null && a.ladder[open] && (
        <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 420, overflow: 'auto' }}>
          {dubai(a.ladder[open].t)}  {nodes.find((n: any) => n.key === a.ladder[open].from)?.ip} → {nodes.find((n: any) => n.key === a.ladder[open].to)?.ip}{'\n\n'}{a.ladder[open].raw}
        </pre>)}
    </div>
  );
}

export default function CallTracePage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [r, setR] = useState<any>(null);
  const [sel, setSel] = useState(0);

  const run = async () => {
    if (!from.trim() || !to.trim()) { setMsg('Enter both numbers.'); return; }
    setBusy(true); setMsg(''); setR(null); setSel(0);
    try { const res = await axios.get('/api/call-trace', { params: { from: from.trim(), to: to.trim(), hours } }); setR(res.data); }
    catch (e) { setMsg(err(e)); }
    setBusy(false);
  };
  const v = r?.verdict;
  const vt = TONE[v?.status || 'skip'];
  const att = r?.attempts?.[sel];

  return (
    <div style={{ padding: 4 }}>
      <div style={box}>
        <h3 style={{ margin: '0 0 4px' }}>Call Trace</h3>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Enter who made the call and the number they dialed. The system checks every step and shows where the call stopped and why, with the SIP ladder of each attempt.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, alignItems: 'end' }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Dialing — extension, user or their number<br />
            <input style={inp} value={from} onChange={(e) => setFrom(e.target.value)} placeholder="e.g. 3000 or +97123074190" onKeyDown={(e) => { if (e.key === 'Enter') run(); }} /></label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Dialed number<br />
            <input style={inp} value={to} onChange={(e) => setTo(e.target.value)} placeholder="e.g. +92 333 0506727" onKeyDown={(e) => { if (e.key === 'Enter') run(); }} /></label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Look back<br />
            <select style={inp} value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              <option value={1}>Last hour</option><option value={6}>Last 6 hours</option><option value={24}>Last 24 hours</option><option value={72}>Last 3 days</option><option value={168}>Last 7 days</option>
            </select></label>
          <div><ZoomBtn variant="primary" onClick={run}>{busy ? 'Tracing…' : 'Trace call'}</ZoomBtn></div>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>For an incoming call, enter the outside caller's number as Dialing and your number as Dialed.</div>
        {msg && <div style={{ color: '#b91c1c', marginTop: 10, fontSize: 13 }}>{msg}</div>}
      </div>

      {r && v && (
        <div style={{ ...box, background: vt.bg, borderColor: vt.fg }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: vt.fg }}>{v.headline}</div>
          {v.where && <div style={{ marginTop: 6, fontSize: 14 }}><b>Where:</b> {v.where}</div>}
          {v.why && <div style={{ marginTop: 4, fontSize: 14 }}><b>Why:</b> {v.why}</div>}
          {v.fix && <div style={{ marginTop: 4, fontSize: 14 }}><b>What to do:</b> {v.fix}</div>}
        </div>)}

      {r && (
        <div style={box}>
          <h4 style={{ margin: '0 0 10px' }}>Checks {r.direction === 'inbound' ? '(incoming call)' : '(outgoing call)'} — as configured now</h4>
          {r.stages.map((s: any) => (
            <div key={s.key} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
              <div style={{ width: 120, fontWeight: 600 }}>{s.label}</div>
              <div style={{ width: 80 }}><ZoomBadge text={TONE[s.status]?.word || s.status} tone={TONE[s.status]?.badge || 'gray'} /></div>
              <div style={{ flex: 1, fontSize: 13 }}>{s.detail}{s.fix && <div style={{ color: TONE[s.status]?.fg, marginTop: 3 }}>{s.fix}</div>}</div>
            </div>))}
        </div>)}

      {r && (
        <div style={box}>
          <h4 style={{ margin: '0 0 10px' }}>Calls found ({r.attempts.length}) — last {r.input.hours} h, newest first</h4>
          {!r.attempts.length && <div style={{ fontSize: 13, color: '#6b7280' }}>No call between these numbers in this period.</div>}
          {r.attempts.map((a: any, i: number) => (
            <div key={i} onClick={() => setSel(i)} style={{ display: 'flex', gap: 12, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: sel === i ? '#eff6ff' : 'transparent', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ width: 150, fontSize: 13 }}>{dubai(a.start)}</div>
              <ZoomBadge text={TONE[a.result.status]?.word || a.result.status} tone={TONE[a.result.status]?.badge || 'gray'} />
              <div style={{ flex: 1, fontSize: 13 }}><b>{a.result.headline}</b>{a.result.where ? ` · ${a.result.where}` : ''}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>dialed as {a.dialed_as} · caller ID {a.caller_id} · {a.duration}s</div>
            </div>))}
        </div>)}

      {att && (
        <div style={box}>
          <h4 style={{ margin: '0 0 4px' }}>Call at {dubai(att.start)} — {att.result.headline}</h4>
          {att.result.why && <div style={{ fontSize: 13, marginBottom: 4 }}>{att.result.why}</div>}
          {att.result.fix && <div style={{ fontSize: 13, color: TONE[att.result.status]?.fg, marginBottom: 10 }}>{att.result.fix}</div>}
          <Ladder a={att} />
        </div>)}
    </div>
  );
}
